import { useState } from 'react'
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { api } from '../../api/client.js'
import { dbFor } from '../../data/db.js'
import { useCustomers } from '../../data/hooks.js'

/**
 * Rattacher un client à une vente qui n'en porte pas.
 *
 * ⭐ L'oubli de comptoir : la vente est passée au pas de course, le client
 * n'a pas été saisi, et on s'en aperçoit après. Sans ce geste, la seule issue
 * était d'annuler et de refaire — c'est-à-dire de percer un trou dans la
 * numérotation légale pour une erreur de saisie.
 *
 * ⚠️ Ne s'ouvre QUE sur un document sans client, et le serveur le revérifie :
 * on comble un blanc, on ne réécrit pas un document validé. Remplacer le
 * client d'une facture émise changerait à qui elle est adressée — ce n'est pas
 * une correction, c'est une falsification. Le remplacement passe par un avoir.
 *
 * ⚠️ Le rattachement passe par le RÉSEAU, sans file d'attente hors ligne.
 * C'est une correction de bureau, pas un geste de caisse : la différer
 * demanderait de fusionner deux écritures concurrentes sur le même document
 * pour un cas qui n'arrive pas au comptoir.
 *
 * ⚠️ La réplique est mise à jour ICI, avec la réponse du serveur — et surtout
 * PAS en déclenchant un cycle de synchronisation. Le moteur écarte les échos
 * de l'appareil qui a écrit, précisément pour ne pas se renvoyer ses propres
 * mutations : l'auteur du rattachement était donc le seul à ne jamais le voir,
 * et la fiche affichait « Comptoir » jusqu'au prochain démarrage.
 */
export function RattacherClientDialog({
  storeId,
  venteId,
  onClose,
  onRattache,
}: {
  storeId: string
  venteId: string
  onClose: () => void
  onRattache: () => void
}): JSX.Element {
  const clients = useCustomers(storeId, { limit: 200 })
  const [clientId, setClientId] = useState('')
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const client = (clients?.data ?? []).find((c) => c.id === clientId) ?? null

  async function rattacher(): Promise<void> {
    if (!client) return
    setOccupe(true)
    setErreur(null)
    try {
      const doc = await api.post<{ customerId: string; customerName: string; version: number }>(
        `/stores/${storeId}/transactions/${venteId}/customer`,
        { customerId: client.id },
      )

      const db = dbFor(storeId)
      const ligne = await db.transactions.get(venteId)
      if (ligne) {
        await db.transactions.put({
          ...ligne,
          customer_id: doc.customerId,
          customer_name: doc.customerName,
          version: doc.version,
        })
      }
      onRattache()
      onClose()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Rattachement impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rattacher un client</DialogTitle>
      <DialogContent>
        {erreur && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {erreur}
          </Alert>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Le nom sera figé sur le document, comme s’il avait été saisi à la vente.
        </Typography>
        <Autocomplete
          options={clients?.data ?? []}
          getOptionLabel={(c) => c.displayName}
          value={client}
          onChange={(_, c) => setClientId(c?.id ?? '')}
          renderInput={(p) => <TextField {...p} label="Client" autoFocus />}
        />
      </DialogContent>
      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!client || occupe} onClick={() => void rattacher()}>
          Rattacher
        </Button>
      </DialogActions>
    </Dialog>
  )
}
